/**
 * Mot cho duy nhat goi backend.
 *
 * 🔴 Duong dan LUON bat dau bang `/api/` — khong bao gio ghi ten mien vao ma
 *    nguon. May dev thi Vite chuyen tiep sang cong 3100; tren VPS thi nginx lam.
 *    Ghi ten mien vao day la den luc doi ten mien phai di sua kh?p noi.
 */

const KHOA_TOKEN = 'mbl.token';

export class LoiApi extends Error {
  constructor(
    readonly ma: number,
    message: string,
    readonly than?: unknown,
  ) {
    super(message);
  }
}

export const layToken = (): string | null => {
  try {
    return localStorage.getItem(KHOA_TOKEN);
  } catch {
    // Trinh duyet chan luu tru (che rieng tu) — van dung duoc, chi la dong lai
    // thi phai dang nhap lai.
    return null;
  }
};

export const datToken = (t: string | null): void => {
  try {
    if (t === null) localStorage.removeItem(KHOA_TOKEN);
    else localStorage.setItem(KHOA_TOKEN, t);
  } catch {
    /* khong luu duoc thi thoi */
  }
};

/** Goi khi token het hieu luc — dat trong `App` de day nguoi dung ve man dang nhap. */
let khiHetPhien: (() => void) | null = null;
export const datKhiHetPhien = (f: () => void): void => {
  khiHetPhien = f;
};

export async function goi<T>(
  duong: string,
  tuyChon: {
    method?: string;
    than?: unknown;
    /**
     * 🔴 Bật cờ này ở những lời gọi mà 401 nghĩa là **mật khẩu vừa gõ sai**,
     *    KHÔNG phải phiên đã hết: đăng nhập, và đổi mật khẩu (backend trả 401
     *    cho "Mật khẩu cũ không đúng").
     *
     *    Không bật thì hai chỗ đó dính xử lý hết-phiên: màn đăng nhập báo
     *    "Phiên đăng nhập đã hết" ngay khi đang đứng ở màn đăng nhập — đã nhìn
     *    thấy thật — và gõ nhầm mật khẩu cũ thì bị **đá ra ngoài** giữa chừng.
     */
    loi401LaSaiMatKhau?: boolean;
  } = {},
): Promise<T> {
  const token = layToken();
  // FormData (upload ảnh, 14/09): KHÔNG đặt Content-Type — trình duyệt tự đặt
  // kèm `boundary`; đặt tay là multer không tách được phần nào ra phần nào.
  const laForm = typeof FormData !== 'undefined' && tuyChon.than instanceof FormData;
  const r = await fetch(duong, {
    method: tuyChon.method ?? 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tuyChon.than !== undefined && !laForm ? { 'Content-Type': 'application/json' } : {}),
    },
    body: laForm ? (tuyChon.than as FormData) : tuyChon.than !== undefined ? JSON.stringify(tuyChon.than) : undefined,
  });

  if (r.status === 401 && !tuyChon.loi401LaSaiMatKhau) {
    // Da chot: khoa tai khoan co hieu luc NGAY. Nen 401 giua chung la chuyen
    // binh thuong, khong phai loi he thong — day ve man dang nhap.
    datToken(null);
    khiHetPhien?.();
    throw new LoiApi(401, 'Phiên đăng nhập đã hết. Đăng nhập lại.');
  }
  // 401 con lai roi xuong duoi va dung NGUYEN cau cua backend
  // ("Sai ten dang nhap hoac mat khau." / "Mat khau cu khong dung.").

  if (r.status === 204) return undefined as T;

  const chu = await r.text();
  let than: unknown;
  try {
    than = chu ? JSON.parse(chu) : null;
  } catch {
    than = chu;
  }

  if (!r.ok) {
    const t = than as { message?: string | string[] } | null;
    const m = Array.isArray(t?.message) ? t.message.join(' · ') : t?.message;
    throw new LoiApi(r.status, m || `Lỗi ${r.status}`, than);
  }
  return than as T;
}

export const tien = (d: number): string => d.toLocaleString('vi-VN') + '\u00a0₫';

/**
 * Tiền viết gọn — CHỈ dùng cho chỗ quá hẹp để chứa số đầy đủ.
 *
 * 🔴 Đây KHÔNG phải bản thay thế của `tien()`. Mọi con số khách phải trả, mọi
 *    con số ghi vào sổ, đều dùng `tien()`. Chỗ duy nhất dùng hàm này là dải
 *    thành tích trên thanh trên của màn tư vấn — bề ngang ở đó chỉ còn ~60–90px.
 *
 * 🔴 Vì sao RÚT GỌN chứ không CẮT BỚT.
 *
 *    Cắt bằng `text-overflow: ellipsis` thì phần mất là ĐUÔI, tức hàng nghìn:
 *    "2.420.878 ₫" cụt thành "2.420…" đọc ra một con số KHÁC HẲN, và không có
 *    dấu hiệu nào nói rằng nó thiếu. "2,4 tr" thì ai cũng đọc ra là xấp xỉ — sai
 *    số có thật nhưng người đọc BIẾT là mình đang đọc số làm tròn.
 *
 *    Số đầy đủ vẫn còn nguyên trong thuộc tính `title` của dải đó.
 */
export const tienGon = (d: number): string => {
  if (d >= 1_000_000) {
    const tr = d / 1_000_000;
    // Dưới 10 triệu thì giữ một chữ số thập phân — chênh lệch 100k ở mức 2,4tr
    // là đáng thấy; ở mức 24tr thì không.
    return (tr < 10 ? tr.toFixed(1).replace('.', ',') : String(Math.round(tr))) + '\u00a0tr';
  }
  if (d >= 1_000) return Math.round(d / 1_000) + 'k';
  return String(d);
};

export const gio = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
    : '—';

/** Bao nhieu phut truoc — dung cho "gio treo lau". */
export const truocDay = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const phut = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (phut < 1) return 'vừa xong';
  if (phut < 60) return `${phut} phút trước`;
  return `${Math.floor(phut / 60)} giờ trước`;
};
